'use client';

import { useEffect } from 'react';
import { useAuthStore, restoreSession, initAuthBroadcast } from '@/hooks/use-auth';
import { useProactiveRefresh } from '@/hooks/use-proactive-refresh';
import { isAuthorFrame, bootDemoSession } from '@/lib/tours/author-frame';

/**
 * Non-blocking session hydrator for the root layout.
 * Attempts to restore auth state from the refresh-token cookie on mount
 * without blocking rendering. Components reading from useAuthStore
 * will re-render when the store updates.
 */
export function SessionHydrator({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  // Root layout: no i18n in scope and nothing blocking, which is exactly what
  // the scheduler needs. It refreshes ahead of expiry so requests stop racing
  // each other on a 401.
  useProactiveRefresh();

  useEffect(() => {
    // Let a sibling tab's successful re-auth dismiss this tab's overlay.
    initAuthBroadcast();
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      // Tier 4 (R2-#1 / F2): in the author iframe hydrate from the demo token,
      // NOT the admin refresh cookie — else the shell renders as admin.
      if (isAuthorFrame()) {
        void bootDemoSession().then((ok) => {
          if (!ok) restoreSession();
        });
      } else {
        restoreSession();
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return <>{children}</>;
}
